// imports
import { useMemo } from 'react';

// local imports
import { DeviceType } from '@graphql/generated-base';
import { useClientReference } from './useClientReference';
import { useGeoBasedLocale } from '@shared/hooks/useGeoLocale';
import { useIsMobileScreen, useIsTabletScreen } from '@shared/utils/media/useMediaScreens';

// interfaces
export interface ApiMetadata {
  deviceType: DeviceType;
  anonymousId: string | undefined;
  eventId: string | undefined;
  userCountryCode: string | undefined;
}

/**
 * Reusable hook that provides common metadata required by accommodation and travel APIs.
 *
 * Returns a memoized metadata object containing:
 * - deviceType: MOBILE, TABLET, or DESKTOP based on screen size
 * - anonymousId: Client anonymous identifier
 * - eventId: Current event identifier
 * - userCountryCode: User's geo-based country code (alpha-2)
 *
 * This standardizes the metadata pattern used across multiple API calls.
 */
export const useApiMetadata = (): ApiMetadata => {
  const isMobile = useIsMobileScreen();
  const isTablet = useIsTabletScreen();
  const { countryCodeAlpha2 } = useGeoBasedLocale();
  const { anonymousId, eventId } = useClientReference();

  return useMemo(() => {
    const deviceType: DeviceType = isMobile ? DeviceType.MOBILE : isTablet ? DeviceType.TABLET : DeviceType.DESKTOP;

    return {
      eventId,
      deviceType,
      anonymousId,
      userCountryCode: countryCodeAlpha2,
    };
  }, [isMobile, isTablet, countryCodeAlpha2, anonymousId, eventId]);
};
